import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import {
    AlertCircle,
    ArrowUpRight,
    Award,
    Bell,
    CheckCircle,
    Clock,
    Filter,
    Loader2,
    Search,
    TrendingUp,
    XCircle,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import useNotifications from '../../hooks/useNotifications.js';
import Pagination from '../../components/common/Pagination';
import '../../styles/UserNotifications.css';

const TYPE_LABELS = {
    ganancia_roi: 'Participación asignada',
    commission: 'Comisión',
    success: 'Confirmación',
    agreement_cancelled: 'Acuerdo cancelado',
    error: 'Error',
};

const TYPE_ICONS = {
    ganancia_roi: TrendingUp,
    commission: Award,
    success: CheckCircle,
    agreement_cancelled: CheckCircle,
    error: XCircle,
};

const getNotificationIcon = (type, isUnread) => {
    const Icon = TYPE_ICONS[type] || Bell;
    const className = `user-notifications__icon${isUnread ? ' user-notifications__icon--unread' : ''}`;
    return <Icon size={18} className={className} />;
};

const formatRelativeTime = (timestamp) => {
    if (!timestamp) {
        return 'Sin fecha';
    }
    const date = typeof timestamp.toDate === 'function' ? timestamp.toDate() : timestamp;
    return formatDistanceToNow(date, { addSuffix: true, locale: es });
};

const Notificaciones = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const {
        notifications,
        loading,
        unreadCount,
        markAsRead,
        markAllAsRead,
    } = useNotifications(currentUser?.uid, false);

    const [statusFilter, setStatusFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    useEffect(() => {
        setCurrentPage(1);
    }, [statusFilter, typeFilter, searchTerm, itemsPerPage]);

    const filteredNotifications = useMemo(() => {
        const search = searchTerm.trim().toLowerCase();
        return notifications.filter((notification) => {
            const matchesStatus =
                statusFilter === 'all'
                    ? true
                    : statusFilter === 'unread'
                        ? !notification.read
                        : notification.read;
            const matchesType = typeFilter === 'all' ? true : notification.type === typeFilter;
            const matchesSearch =
                search.length === 0 ||
                [notification.title, notification.message, notification.link]
                    .filter(Boolean)
                    .some((value) => value.toLowerCase().includes(search));
            return matchesStatus && matchesType && matchesSearch;
        });
    }, [notifications, statusFilter, typeFilter, searchTerm]);

    useEffect(() => {
        const maxPage = Math.max(1, Math.ceil(filteredNotifications.length / itemsPerPage) || 1);
        if (currentPage > maxPage) {
            setCurrentPage(maxPage);
        }
    }, [filteredNotifications.length, itemsPerPage, currentPage]);

    const paginatedNotifications = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredNotifications.slice(start, start + itemsPerPage);
    }, [filteredNotifications, currentPage, itemsPerPage]);

    const typeOptions = useMemo(() => {
        const values = new Set();
        notifications.forEach((notification) => {
            if (notification.type) {
                values.add(notification.type);
            }
        });
        return Array.from(values);
    }, [notifications]);

    const stats = useMemo(() => ({
        total: notifications.length,
        unread: unreadCount,
        read: notifications.length - unreadCount,
    }), [notifications.length, unreadCount]);

    const totalPages = Math.max(1, Math.ceil(filteredNotifications.length / itemsPerPage) || 1);

    const handleItemClick = (notification) => {
        if (!notification.read) {
            markAsRead(notification.id);
        }
    };

    const handleNavigateToLink = (link) => {
        if (link) {
            navigate(`/dashboard/${link}`);
        }
    };

    const handleMarkAllAsRead = () => {
        if (unreadCount > 0) {
            markAllAsRead();
        }
    };

    const renderStatusBadge = (isRead) => (
        <span className={`user-notifications__status-badge${isRead ? ' is-read' : ' is-unread'}`}>
            {isRead ? 'Leída' : 'No leída'}
        </span>
    );

    const humanizeKey = (key = '') => key
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^\w/, (char) => char.toUpperCase()) || 'Dato';

    const formatMetadataValue = (value) => {
        if (value === null || value === undefined) return 'Sin dato';
        if (typeof value === 'number') return value.toLocaleString('es-ES');
        if (typeof value === 'boolean') return value ? 'Sí' : 'No';
        if (value instanceof Date) {
            return value.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
        }
        if (typeof value === 'object') {
            return Array.isArray(value) ? value.join(', ') : 'Consulta detalle';
        }
        return String(value);
    };

    const renderMetadataPreview = (metadata) => {
        if (!metadata) return null;
        const entries = Object.entries(metadata).slice(0, 3);
        if (entries.length === 0) return null;
        const remaining = Object.keys(metadata).length - entries.length;
        return (
            <div className="user-notifications__metadata-preview">
                {entries.map(([key, value]) => (
                    <div key={key}>
                        <span className="label">{humanizeKey(key)}</span>
                        <p>{formatMetadataValue(value)}</p>
                    </div>
                ))}
                {remaining > 0 && (
                    <span className="user-notifications__metadata-more">+{remaining} datos adicionales</span>
                )}
            </div>
        );
    };

    return (
        <div className="user-notifications">
            <header className="user-notifications__header">
                <div className="user-notifications__title">
                    <h2><Bell size={20} /> Centro de notificaciones</h2>
                    <p>Consulta las alertas de tus participaciones y acciones dentro de la plataforma.</p>
                </div>
                <button
                    type="button"
                    onClick={handleMarkAllAsRead}
                    className="user-notifications__action"
                    disabled={unreadCount === 0}
                >
                    <CheckCircle size={16} /> Marcar todas como leídas ({unreadCount})
                </button>
            </header>

            <section className="user-notifications__stats">
                <div className="user-notifications__stats-item">
                    <Bell size={16} />
                    <span>Total</span>
                    <strong>{stats.total}</strong>
                </div>
                <div className="user-notifications__stats-item">
                    <Clock size={16} />
                    <span>No leídas</span>
                    <strong>{stats.unread}</strong>
                </div>
                <div className="user-notifications__stats-item">
                    <CheckCircle size={16} />
                    <span>Leídas</span>
                    <strong>{stats.read}</strong>
                </div>
            </section>

            <section className="user-notifications__controls">
                <div className="user-notifications__search">
                    <Search size={16} />
                    <input
                        type="search"
                        placeholder="Buscar por título, mensaje o enlace"
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                    />
                </div>
                <div className="user-notifications__filters">
                    <Filter size={16} />
                    <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                        <option value="all">Todos los estados</option>
                        <option value="unread">No leídas</option>
                        <option value="read">Leídas</option>
                    </select>
                    <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
                        <option value="all">Todos los tipos</option>
                        {typeOptions.map((type) => (
                            <option key={type} value={type}>
                                {TYPE_LABELS[type] || type}
                            </option>
                        ))}
                    </select>
                </div>
            </section>

            <div className="user-notifications__layout">
                <section className="user-notifications__list">
                    {loading ? (
                        <div className="user-notifications__loading">
                            <Loader2 className="spin" size={20} />
                            <span>Cargando notificaciones...</span>
                        </div>
                    ) : paginatedNotifications.length === 0 ? (
                        <div className="user-notifications__empty">
                            <AlertCircle size={32} />
                            <p>No encontramos notificaciones con los filtros aplicados.</p>
                        </div>
                    ) : (
                        paginatedNotifications.map((notification) => (
                            <button
                                key={notification.id}
                                type="button"
                                className={`user-notifications__item${!notification.read ? ' is-unread' : ''}`}
                                onClick={() => handleItemClick(notification)}
                            >
                                <div className="user-notifications__item-icon">
                                    {getNotificationIcon(notification.type, !notification.read)}
                                </div>
                                <div className="user-notifications__item-content">
                                    <div className="user-notifications__item-header">
                                        <h3>{notification.title || TYPE_LABELS[notification.type] || 'Notificación'}</h3>
                                        {renderStatusBadge(notification.read)}
                                    </div>
                                    <p>{notification.message || 'Sin detalles adicionales.'}</p>
                                    {renderMetadataPreview(notification.metadata)}
                                    <div className="user-notifications__item-meta">
                                        <span>{formatRelativeTime(notification.createdAt)}</span>
                                        {notification.type && (
                                            <span className="user-notifications__type-chip">{TYPE_LABELS[notification.type] || notification.type}</span>
                                        )}
                                        {notification.link && (
                                            <button
                                                type="button"
                                                className="user-notifications__inline-cta"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    handleNavigateToLink(notification.link);
                                                }}
                                            >
                                                Ver detalle <ArrowUpRight size={14} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </button>
                        ))
                    )}

                    {!loading && filteredNotifications.length > itemsPerPage && (
                        <div className="user-notifications__pagination">
                            <Pagination
                                totalItems={filteredNotifications.length}
                                itemsPerPage={itemsPerPage}
                                onItemsPerPageChange={setItemsPerPage}
                                currentPage={currentPage}
                                onNext={() => setCurrentPage((page) => Math.min(page + 1, totalPages))}
                                onPrevious={() => setCurrentPage((page) => Math.max(page - 1, 1))}
                                canNext={currentPage < totalPages}
                                canPrevious={currentPage > 1}
                            />
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
};

export default Notificaciones;